import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

const initialState = {
  searchFollower: {},
  followers: [],
  followerCount: 0,
  page: 0,
  activeFilter: 'pending',
  searchQuery: '',
  advancedFilters: [],
  followersLoading: false,
  followersError: null,
  totals: {
    all: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    // Self-service totals
    sent: 0,
    skipped: 0,
    not_found: 0,
  },
  // History-specific state
  historyDate: new Date(),
  historyActionType: 'approved', // 'approved', 'rejected'
  historySummary: null,
  messages_pending: 0,
};

const useFollowerStore = create(
  devtools(
    (set, get) => ({
      ...initialState,

      setSearchFollower: (searchFollower) => set({ searchFollower }),

      // Followers pagination - same pattern as InboxStore
      setFollowers: (followers, page, count) =>
        set({
          followers,
          page,
          followerCount: count,
        }),
      setNextFollowers: (followers, page, count) =>
        set((state) => ({
          followers: [...state.followers, ...followers],
          page: page,
          followerCount: count,
        })),

      setFollowersLoading: (loading) => set({ followersLoading: loading }),

      setFollowersError: (error) => set({ followersError: error }),

      setActiveFilter: (filter) => set({ activeFilter: filter }),

      setSearchQuery: (query) => set({ searchQuery: query }),

      setAdvancedFilters: (filters) => set({ advancedFilters: filters }),

      // Set totals from API response
      // Handles both regular opens (approved, rejected) and self-service (sent, skipped, not_found)
      setTotals: (totals) =>
        set((state) => ({
          totals: {
            ...state.totals,
            ...totals,
          },
        })),

      // Update totals when followers are processed
      updateTotalsOnAction: (processedIds, action, previousStatuses = {}) =>
        set((state) => {
          const { totals } = state;
          const newTotals = { ...totals };

          processedIds.forEach((followerId) => {
            const previousStatus = previousStatuses[followerId] || 'pending';
            const newStatus = action === 'approve' ? 'approved' : 'rejected';

            // Decrease count for previous status
            if (previousStatus === 'pending') {
              newTotals.pending = Math.max(0, newTotals.pending - 1);
            } else if (previousStatus === 'approved') {
              newTotals.approved = Math.max(0, newTotals.approved - 1);
            } else if (previousStatus === 'rejected') {
              newTotals.rejected = Math.max(0, newTotals.rejected - 1);
            }

            // Increase count for new status
            if (newStatus === 'approved') {
              newTotals.approved += 1;
            } else if (newStatus === 'rejected') {
              newTotals.rejected += 1;
            }
          });

          return { totals: newTotals };
        }),

      approveFollower: (followerId) =>
        set((state) => ({
          followers: state.followers.map((follower) =>
            follower._id === followerId
              ? {
                  ...follower,
                  personalized_action_status: 'approved',
                }
              : follower
          ),
        })),

      rejectFollower: (followerId) =>
        set((state) => ({
          followers: state.followers.map((follower) =>
            follower._id === followerId
              ? {
                  ...follower,
                  personalized_action_status: 'rejected',
                }
              : follower
          ),
        })),

      resetToPending: (followerId) =>
        set((state) => ({
          followers: state.followers.map((follower) =>
            follower._id === followerId
              ? {
                  ...follower,
                  personalized_action_status: 'pending',
                }
              : follower
          ),
        })),

      changeStatus: (followerId, newStatus) =>
        set((state) => ({
          followers: state.followers.map((follower) =>
            follower._id === followerId
              ? {
                  ...follower,
                  personalized_action_status: newStatus,
                }
              : follower
          ),
        })),

      bulkChangeStatus: (followerIds, newStatus) =>
        set((state) => ({
          followers: state.followers.map((follower) =>
            followerIds.includes(follower._id)
              ? {
                  ...follower,
                  personalized_action_status: newStatus,
                }
              : follower
          ),
        })),

      updatePersonalizedOpening: (followerId, message) =>
        set((state) => ({
          followers: state.followers.map((follower) =>
            follower._id === followerId
              ? {
                  ...follower,
                  opens_message: {
                    ...follower.opens_message,
                    text: message,
                  },
                }
              : follower
          ),
        })),

      updateMessageOlderThanPrompt: (followerId, value) =>
        set((state) => ({
          followers: state.followers.map((follower) =>
            follower._id === followerId
              ? {
                  ...follower,
                  message_older_than_prompt: value,
                }
              : follower
          ),
        })),

      // New method to update followers based on API response
      updateFollowersFromResponse: (processedIds, unprocessedIds, newStatus) =>
        set((state) => ({
          followers: state.followers.map((follower) => {
            if (processedIds.includes(follower._id)) {
              return {
                ...follower,
                personalized_action_status: newStatus,
              };
            }
            return follower;
          }),
        })),

      // New method to remove followers from store
      removeFollowersFromStore: (followerIds) =>
        set((state) => ({
          followers: state.followers.filter(
            (follower) => !followerIds.includes(follower._id)
          ),
        })),

      getFilteredFollowers: () => {
        const { followers } = get();
        // Search filtering is now handled by the API, so we just return all followers
        return followers;
      },

      // History-specific actions
      setHistoryDate: (date) => set({ historyDate: date }),

      setHistoryActionType: (actionType) =>
        set({ historyActionType: actionType }),

      setHistorySummary: (summary) => set({ historySummary: summary }),

      setMessagesPending: (count) => set({ messages_pending: count }),

      // Self-service opens specific methods
      // Individual follower action methods for self-service
      sentFollower: (followerId) =>
        set((state) => ({
          followers: state.followers.map((follower) =>
            follower._id === followerId
              ? {
                  ...follower,
                  personalized_action_status: 'sent',
                }
              : follower
          ),
        })),

      skippedFollower: (followerId) =>
        set((state) => ({
          followers: state.followers.map((follower) =>
            follower._id === followerId
              ? {
                  ...follower,
                  personalized_action_status: 'skipped',
                }
              : follower
          ),
        })),

      notFoundFollower: (followerId) =>
        set((state) => ({
          followers: state.followers.map((follower) =>
            follower._id === followerId
              ? {
                  ...follower,
                  personalized_action_status: 'not_found',
                }
              : follower
          ),
        })),

      // Bulk self-service actions
      bulkSentFollowers: (followerIds) =>
        set((state) => ({
          followers: state.followers.map((follower) =>
            followerIds.includes(follower._id)
              ? {
                  ...follower,
                  personalized_action_status: 'sent',
                }
              : follower
          ),
        })),

      bulkSkippedFollowers: (followerIds) =>
        set((state) => ({
          followers: state.followers.map((follower) =>
            followerIds.includes(follower._id)
              ? {
                  ...follower,
                  personalized_action_status: 'skipped',
                }
              : follower
          ),
        })),

      bulkNotFoundFollowers: (followerIds) =>
        set((state) => ({
          followers: state.followers.map((follower) =>
            followerIds.includes(follower._id)
              ? {
                  ...follower,
                  personalized_action_status: 'not_found',
                }
              : follower
          ),
        })),

      // Update totals when self-service actions are processed (mark_sent, skip, not_found)
      updateTotalsOnSelfServiceAction: (
        processedIds,
        action,
        previousStatuses = {}
      ) =>
        set((state) => {
          const { totals } = state;
          const newTotals = { ...totals };

          // Map action to status for self-service
          const actionToStatusMap = {
            mark_sent: 'sent',
            skip: 'skipped',
            not_found: 'not_found',
          };
          const newStatus = actionToStatusMap[action] || action;

          processedIds.forEach((followerId) => {
            const previousStatus = previousStatuses[followerId] || 'pending';

            // Decrease count for previous status
            if (
              previousStatus === 'pending' &&
              newTotals.pending !== undefined
            ) {
              newTotals.pending = Math.max(0, newTotals.pending - 1);
            } else if (
              previousStatus === 'sent' &&
              newTotals.sent !== undefined
            ) {
              newTotals.sent = Math.max(0, newTotals.sent - 1);
            } else if (
              previousStatus === 'skipped' &&
              newTotals.skipped !== undefined
            ) {
              newTotals.skipped = Math.max(0, newTotals.skipped - 1);
            } else if (
              previousStatus === 'not_found' &&
              newTotals.not_found !== undefined
            ) {
              newTotals.not_found = Math.max(0, newTotals.not_found - 1);
            }

            // Increase count for new status
            if (newStatus === 'sent' && newTotals.sent !== undefined) {
              newTotals.sent += 1;
            } else if (
              newStatus === 'skipped' &&
              newTotals.skipped !== undefined
            ) {
              newTotals.skipped += 1;
            } else if (
              newStatus === 'not_found' &&
              newTotals.not_found !== undefined
            ) {
              newTotals.not_found += 1;
            }
          });

          return { totals: newTotals };
        }),
    }),
    {
      name: 'follower-store',
    }
  )
);

export default useFollowerStore;
